/**
 * Originally from: https://github.com/timdeschryver/ngrx-etc
 *
 * Extended with "success action types" and "error action types".
 */

import { ActionCreator, createAction, Creator } from '@ngrx/store';
import { Action, FunctionWithParametersType, TypedAction } from '@ngrx/store/src/models';
import { v4 as uuidV4 } from 'uuid';

const successActionTypes = {};
const errorActionTypes = {};

export interface CorrelationAttributes {
  __correlationId: string,
  __successActionType: string,
  __errorActionType: string
}

export type ActionWithCorrelationAttributes = Action & CorrelationAttributes;

export interface TypedActionWithCorrelation<T extends string> extends TypedAction<T>, CorrelationAttributes {
  __correlationId: string,
  __successActionType: string,
  __errorActionType: string
}

export function createActionWithCorrelation<T extends string>(
  type: T,
  successAction?: string,
  errorAction?: string,
): ActionCreator<T, () => TypedActionWithCorrelation<T>>

export function createActionWithCorrelation<T extends string, P extends object>(
  type: T,
  config: {
    _as: 'props'
    _p: P
  },
  successAction?: string,
  errorAction?: string,
): ActionCreator<T, (props: P) => P & TypedActionWithCorrelation<T>>

export function createActionWithCorrelation<T extends string, P extends any[], R extends object>(
  type: T,
  creator: Creator<P>,
  successAction?: string,
  errorAction?: string,
): FunctionWithParametersType<P, R & TypedActionWithCorrelation<T>> & TypedActionWithCorrelation<T>

export function createActionWithCorrelation<T extends string, P extends any[], R extends object>(...args: any[]): any {
  const shift = (typeof args[1] === 'object') || (typeof args[1] === 'function') ? 0 : 1;
  successActionTypes[args[0]] = args[2 - shift];
  errorActionTypes[args[0]] = args[3 - shift];
  args = args.filter((a, i) => {
    if (i === 0)
      return true;
    return (typeof a === 'object') || (typeof a === 'function');
  });
  return correlateAction((createAction as any)(...args))
}

export function correlateAction<T extends string>(
  actionCreator: ActionCreator<T, () => TypedAction<T>>,
): ActionCreator<T, () => TypedActionWithCorrelation<T>>
export function correlateAction<T extends string, P extends object>(
  actionCreator: ActionCreator<T, (props: P) => P & TypedAction<T>>,
): ActionCreator<T, (props: P) => P & TypedActionWithCorrelation<T>>
export function correlateAction<T extends string, P extends any[], R extends object>(
  actionCreator: FunctionWithParametersType<P, R & TypedAction<T>> & TypedAction<T>,
): FunctionWithParametersType<P, R & TypedActionWithCorrelation<T>> & TypedActionWithCorrelation<T>

export function correlateAction<T extends string>(actionCreator: any): any {
  const creator = function correlate(...args: any[]): TypedActionWithCorrelation<T> {
    const result = actionCreator(...args);
    const actions = {
      __successActionType: successActionTypes[result.type],
      __errorActionType: errorActionTypes[result.type]
    };
    if (actions.__successActionType === undefined) {
      delete actions.__successActionType;
    }
    if (actions.__errorActionType === undefined) {
      delete actions.__errorActionType;
    }
    return {
      ...result,
      ...actions,
      __correlationId: result.__correlationId ? result.__correlationId : uuidV4()
    }
  };
  defineType(actionCreator.type, creator);
  return creator
}

function defineType(type: string, creator: Creator): Creator {
  return Object.defineProperty(creator, 'type', {
    value: type,
    writable: false,
  })
}
